import { FC, Dispatch, SetStateAction } from 'react'
import { Box, InputAdornment, Skeleton, TextField, Typography } from '@mui/material'
import theme from 'theme'

/* Utils */
import { SSMCell, SSMProductStatus, Vendor } from 'models'
import { getVendorLogo } from '../../../../../lib/utils'

/* Components */
import {
  NoResultsList,
  SearchProductsContainer,
  AutocompleteContainer,
  ResultListItem,
  ResultsList,
  SearchVendorsInputWrapper,
  NoIntegrationLogo,
  SearchVendorsResultsContainer
} from './searchProducts.styles'
import { SSMCardSkeleton, SSMCardSkeletonWrapper } from '../../components/ssmCard.styles'
import ListItemDisabled from '../../components/ListItemDisabled'
import { IconTooltip } from '../../components/IconTooltip'

interface Props {
  loading: boolean
  vendors: any
  initialSearched: any
  setBackspacingInput: any
  inputValue: any
  getRootProps: any
  focused: any
  getInputProps: any
  setVendors: any
  emptyValue: any
  setInputValue: any
  setLoading: any
  debouncedSearch: any
  drawerProps: any
  handleClearSearch: any
  getListboxProps: any
  getOptionProps: any
  handleListKeyDown: any
  recentProducts: any
  allProducts: any
  buttonWidth?: number
  handleProductSelect: (vendor: Vendor | null) => void
  cell: SSMCell | null
  setAddingCustomVendor: Dispatch<SetStateAction<boolean>>
  setProductPopupOpen: Dispatch<SetStateAction<boolean>>
}

const SearchProducts: FC<Props> = ({
  loading,
  vendors,
  initialSearched,
  getRootProps,
  inputValue,
  setBackspacingInput,
  focused,
  getInputProps,
  setVendors,
  emptyValue,
  setInputValue,
  setLoading,
  debouncedSearch,
  drawerProps,
  handleClearSearch,
  getListboxProps,
  getOptionProps,
  handleListKeyDown,
  recentProducts,
  allProducts,
  buttonWidth,
  handleProductSelect,
  cell,
  setAddingCustomVendor,
  setProductPopupOpen
}) => {
  return (
    <SearchProductsContainer
      height={
        (initialSearched && !loading && vendors.length === 1) ||
        (initialSearched && !loading && inputValue === '' && vendors.length > 1)
          ? '228px'
          : '311px'
      }
      width={buttonWidth}
    >
      <AutocompleteContainer>
        <SearchVendorsInputWrapper
          {...getRootProps()}
          className={focused ? 'focused' : ''}
          hasinputvalue={inputValue.length > 0 ? 'true' : 'false'}
        >
          <TextField
            variant="outlined"
            color="primary"
            size="medium"
            label=""
            placeholder="Search"
            inputProps={{ ...getInputProps() }}
            InputProps={{
              onKeyDown: (e: any) => {
                if (e.keyCode === 8) {
                  e.preventDefault()
                  setBackspacingInput(true)
                  if (inputValue.length <= 1) {
                    setVendors([emptyValue])
                  }
                  setInputValue((pre: any) => pre.substring(0, pre.length - 1))
                }
              },
              onKeyUp: (e: any) => {
                if (e.keyCode === 8) {
                  setBackspacingInput(false)
                  if (inputValue.length >= 2) {
                    setLoading(true)
                    debouncedSearch(inputValue, drawerProps)
                  }
                }
              },
              startAdornment: (
                <InputAdornment position="start">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M9 3.5C5.96243 3.5 3.5 5.96243 3.5 9C3.5 12.0376 5.96243 14.5 9 14.5C10.519 14.5 11.893 13.8852 12.8891 12.8891C13.8852 11.893 14.5 10.519 14.5 9C14.5 5.96243 12.0376 3.5 9 3.5ZM2 9C2 5.13401 5.13401 2 9 2C12.866 2 16 5.13401 16 9C16 10.6625 15.4197 12.1906 14.4517 13.3911L17.7803 16.7197C18.0732 17.0126 18.0732 17.4874 17.7803 17.7803C17.4874 18.0732 17.0126 18.0732 16.7197 17.7803L13.3911 14.4517C12.1906 15.4197 10.6625 16 9 16C5.13401 16 2 12.866 2 9Z"
                      fill="#D0C3CC"
                    />
                  </svg>
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end" onClick={handleClearSearch}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M20 5.61143L18.3886 4L12 10.3886L5.61143 4L4 5.61143L10.3886 12L4 18.3886L5.61143 20L12 13.6114L18.3886 20L20 18.3886L13.6114 12L20 5.61143Z"
                      fill={theme.colors.textSecondaryLow}
                    />
                  </svg>
                </InputAdornment>
              )
            }}
          />
        </SearchVendorsInputWrapper>

        {/* Loading */}
        {focused && loading && (
          <SearchVendorsResultsContainer>
            <SSMCardSkeletonWrapper sx={{ gap: `${theme.spacing.xSmall}px !important` }}>
              {Array.from(Array(5).keys()).map((n) => (
                <SSMCardSkeleton sx={{ height: '40px !important' }} key={n}>
                  <Skeleton
                    variant="rectangular"
                    component="div"
                    animation="wave"
                    width="100%"
                    height="100%"
                    className="card-skeleton-inner"
                  />
                </SSMCardSkeleton>
              ))}
            </SSMCardSkeletonWrapper>
          </SearchVendorsResultsContainer>
        )}

        {/* No results found */}
        {((initialSearched && !loading && inputValue === '' && vendors.length > 1) ||
          (initialSearched &&
            !loading &&
            inputValue.length > 1 &&
            vendors.length === 1 &&
            vendors[0].display_name === '')) && (
          <SearchVendorsResultsContainer>
            <Box className="search-vendors-empty-wrapper">
              <Typography className="search-vendors-empty-title">No matches found</Typography>
            </Box>
            <NoResultsList
              {...getListboxProps()}
              hideborder="true"
              length={0}
              className="search-vendors-empty-text-wrapper"
            >
              <Typography className="search-vendors-empty-text">
                Check the spelling or{' '}
                <span
                  className="here-text"
                  onClick={() => {
                    setAddingCustomVendor(true)
                    setProductPopupOpen(false)
                  }}
                >
                  Add
                </span>{' '}
                a new product or vendor manually
              </Typography>
            </NoResultsList>
          </SearchVendorsResultsContainer>
        )}

        {/* Results */}
        {vendors.length > 0 &&
          !loading &&
          !(
            vendors.length === 1 &&
            (vendors as Vendor[])[0].display_name === '' &&
            (vendors as Vendor[])[0].name === ''
          ) && (
            <SearchVendorsResultsContainer>
              <Typography className="search-vendors-results-text">Results ({vendors.length})</Typography>
              <ResultsList {...getListboxProps()} items={vendors.length} onKeyDown={handleListKeyDown}>
                {(vendors as Vendor[]).map((option, index) => {
                  if (!option.display_name || !option.name) return null
                  const vendor = option.is_product ? option.vendor : option
                  const product = option.is_product ? option : null

                  if (option.existsInCell) {
                    const optionProps = {
                      ...getOptionProps({ option, index })
                    }

                    return (
                      <ListItemDisabled
                        key={index}
                        option={option}
                        optionProps={optionProps}
                        getVendorLogo={getVendorLogo}
                        vendor={vendor}
                        product={product}
                      />
                    )
                  }
                  return (
                    <ResultListItem
                      {...getOptionProps({ option, index })}
                      key={index}
                      onClick={() => {
                        handleProductSelect(option)
                      }}
                    >
                      <div style={{ position: 'relative' }}>
                        <IconTooltip
                          placement="left-start"
                          PopperProps={{
                            sx: {
                              marginTop: `-${theme.spacing.xSmall}px !important`,
                              marginRight: '7px !important'
                            }
                          }}
                          title={
                            <>
                              <Box className="tooltip-wrapper">
                                <svg
                                  width="12"
                                  height="14"
                                  viewBox="0 0 12 14"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="arrow-right"
                                >
                                  <path d="M12 7.0718L0 0.143593V14L12 7.0718Z" fill={theme.colors.controlBorder} />
                                </svg>
                                {option.is_product && option.vendor && (
                                  <Typography className="tooltip-text">{option.vendor.display_name}</Typography>
                                )}
                                <Typography
                                  className={option.is_product && option.vendor ? 'tooltip-subtext' : 'tooltip-text'}
                                >
                                  {option.display_name}
                                </Typography>
                              </Box>
                            </>
                          }
                        >
                          <Box className="list-item-wrapper">
                            {option.logo_path && option.logo_path.length > 0 && option.logo_path !== 'N/A' ? (
                              <img
                                src={getVendorLogo(option.logo_path)}
                                width="24px"
                                height="24px"
                                alt={option.display_name}
                              />
                            ) : (
                              <NoIntegrationLogo>
                                <Typography className="no-integration-logo-letter">
                                  {option.vendor && option.vendor.display_name
                                    ? option.vendor.display_name[0]
                                    : option.display_name[0]}
                                </Typography>
                              </NoIntegrationLogo>
                            )}

                            <Typography className="option-text">
                              {vendor?.display_name || ''}
                              {product && ` ${product.display_name}`}
                            </Typography>
                          </Box>
                        </IconTooltip>
                      </div>
                    </ResultListItem>
                  )
                })}
              </ResultsList>
            </SearchVendorsResultsContainer>
          )}

        {/* Recent Products Results */}
        {(!initialSearched &&
          !loading &&
          vendors.length === 1 &&
          (vendors as Vendor[])[0].display_name === '' &&
          (vendors as Vendor[])[0].name === '') ||
        (initialSearched &&
          inputValue.length < 2 &&
          !loading &&
          vendors.length === 1 &&
          vendors[0].display_name === '') ? (
          <>
            {recentProducts && recentProducts.length > 0 ? (
              <SearchVendorsResultsContainer>
                <Typography className="search-vendors-results-text">Recents ({recentProducts.length})</Typography>
                <ResultsList items={recentProducts.length}>
                  {(recentProducts as Vendor[]).map((option, index) => {
                    if (!option.display_name && option.name) return null
                    const vendor = option.is_product ? option.vendor : option
                    const product = option.is_product ? option : null
                    const productExistsInCell = cell?.products.find(
                      (n: any) => n.uuid === option.uuid && n.status !== SSMProductStatus.Ended
                    )

                    if (productExistsInCell) {
                      const optionProps = {
                        ...getOptionProps({ option, index })
                      }

                      return (
                        <ListItemDisabled
                          key={index}
                          option={option}
                          optionProps={optionProps}
                          getVendorLogo={getVendorLogo}
                          vendor={vendor}
                          product={product}
                        />
                      )
                    }
                    return (
                      <ResultListItem
                        {...getOptionProps({ option, index })}
                        key={index}
                        onClick={() => {
                          if (!productExistsInCell) {
                            handleProductSelect(option)
                          }
                        }}
                      >
                        <div style={{ position: 'relative' }}>
                          <IconTooltip
                            placement="left-start"
                            PopperProps={{
                              sx: {
                                marginTop: '-8px !important',
                                marginRight: '7px !important'
                              }
                            }}
                            title={
                              <>
                                <Box className="tooltip-wrapper">
                                  <svg
                                    width="12"
                                    height="14"
                                    viewBox="0 0 12 14"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className="arrow-right"
                                  >
                                    <path d="M12 7.0718L0 0.143593V14L12 7.0718Z" fill={theme.colors.controlBorder} />
                                  </svg>
                                  {option.is_product && option.vendor && (
                                    <Typography className="tooltip-text">{option.vendor.display_name}</Typography>
                                  )}
                                  <Typography
                                    className={option.is_product && option.vendor ? 'tooltip-subtext' : 'tooltip-text'}
                                  >
                                    {option.display_name}
                                  </Typography>
                                </Box>
                              </>
                            }
                          >
                            <Box className="list-item-wrapper">
                              {option.logo_path && option.logo_path.length > 0 && !option.logo_path.includes('N/A') ? (
                                <img
                                  src={getVendorLogo(option.logo_path)}
                                  width="24px"
                                  height="24px"
                                  alt={option.display_name}
                                />
                              ) : (
                                <NoIntegrationLogo>
                                  <Typography className="no-integration-logo-letter">
                                    {option.vendor && option.vendor.display_name
                                      ? option.vendor.display_name[0]
                                      : option.display_name
                                      ? option.display_name[0]
                                      : ''}
                                  </Typography>
                                </NoIntegrationLogo>
                              )}
                              <Typography className="option-text">
                                {vendor?.display_name || ''}
                                {product && ` ${product.display_name || ''}`}
                              </Typography>
                            </Box>
                          </IconTooltip>
                        </div>
                      </ResultListItem>
                    )
                  })}
                </ResultsList>
              </SearchVendorsResultsContainer>
            ) : (
              <>
                {allProducts.length > 0 && (
                  <SearchVendorsResultsContainer>
                    <Typography className="search-vendors-results-text">
                      Recents ({allProducts.filter((n: any, i: any) => i < 5).length})
                    </Typography>
                    <ResultsList items={allProducts.filter((n: any, i: any) => i < 5).length}>
                      {(allProducts as Vendor[])
                        .filter((n, i) => i < 5)
                        .map((option, index) => {
                          if (!option.display_name && option.name) return null
                          const vendor = option.is_product ? option.vendor : option
                          const product = option.is_product ? option : null
                          const productExistsInCell = cell?.products.find(
                            (n: any) => n.uuid === option.uuid && n.status !== SSMProductStatus.Ended
                          )

                          if (productExistsInCell) {
                            const optionProps = {
                              ...getOptionProps({ option, index })
                            }

                            return (
                              <ListItemDisabled
                                key={index}
                                option={option}
                                optionProps={optionProps}
                                getVendorLogo={getVendorLogo}
                                vendor={vendor}
                                product={product}
                              />
                            )
                          }
                          return (
                            <ResultListItem
                              {...getOptionProps({ option, index })}
                              key={index}
                              onClick={() => {
                                if (!productExistsInCell) {
                                  handleProductSelect(option)
                                }
                              }}
                            >
                              <>
                                <IconTooltip
                                  placement="left-start"
                                  PopperProps={{
                                    sx: {
                                      marginTop: '-8px !important',
                                      marginRight: '7px !important'
                                    }
                                  }}
                                  title={
                                    <>
                                      <Box className="tooltip-wrapper">
                                        <svg
                                          width="12"
                                          height="14"
                                          viewBox="0 0 12 14"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                          className="arrow-right"
                                        >
                                          <path
                                            d="M12 7.0718L0 0.143593V14L12 7.0718Z"
                                            fill={theme.colors.controlBorder}
                                          />
                                        </svg>
                                        {option.is_product && option.vendor && (
                                          <Typography className="tooltip-text">{option.vendor.display_name}</Typography>
                                        )}
                                        <Typography
                                          className={
                                            option.is_product && option.vendor ? 'tooltip-subtext' : 'tooltip-text'
                                          }
                                        >
                                          {option.display_name}
                                        </Typography>
                                      </Box>
                                    </>
                                  }
                                >
                                  <Box className="list-item-wrapper">
                                    {option.logo_path && option.logo_path.length > 0 ? (
                                      <img
                                        src={getVendorLogo(option.logo_path)}
                                        width="24px"
                                        height="24px"
                                        alt={option.display_name}
                                      />
                                    ) : (
                                      <NoIntegrationLogo>
                                        <Typography className="no-integration-logo-letter">
                                          {option.vendor && option.vendor.display_name
                                            ? option.vendor.display_name[0]
                                            : option.display_name[0]}
                                        </Typography>
                                      </NoIntegrationLogo>
                                    )}
                                    <Typography className="option-text">
                                      {vendor?.display_name || ''}
                                      {product && ` ${product.display_name}`}
                                    </Typography>
                                  </Box>
                                </IconTooltip>
                              </>
                            </ResultListItem>
                          )
                        })}
                    </ResultsList>
                  </SearchVendorsResultsContainer>
                )}
              </>
            )}
          </>
        ) : (
          <></>
        )}
      </AutocompleteContainer>
    </SearchProductsContainer>
  )
}

export default SearchProducts
